import './styles.css';

const MAX_PDF_BYTES = 50 * 1024 * 1024;
const MAX_PDF_FILES = 8;
const MAX_PDF_PAGES = 200;
const MAX_OUTPUT_PAGES = 400;
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
const MAX_IMAGE_FILES = 20;
const MAX_IMAGE_TOTAL_BYTES = 100 * 1024 * 1024;
const MAX_IMAGE_PIXELS = 80 * 1000 * 1000;
const MAX_IMAGE_TOTAL_PIXELS = 240 * 1000 * 1000;
const THUMBNAIL_WIDTH = 120;
const PDFJS_ASSET_BASE = '/assets/pdfjs/';

const route = document.body.dataset.pdfTool || 'home';
const app = document.querySelector('#app');

const TOOL_CONFIG = {
  merge: {
    eyebrow: 'Kivoza document tools · PDF',
    title: 'Merge PDFs in your browser.',
    lede: 'Put documents in the order you want, then create one local PDF. Your files stay in this browser tab.',
    intro: 'Add up to 8 PDFs. We check the bytes and page count before they enter the queue.',
    trust: ['Local processing', 'No upload', 'One PDF out'],
    workspaceEyebrow: 'Build one document',
    workspaceTitle: 'Choose your files.',
    steps: [
      ['Add', 'Select PDFs or drop them onto the queue. Each document is checked before it is accepted.'],
      ['Arrange', 'Use the keyboard-friendly move controls to set the document order.'],
      ['Download', 'Create one PDF, inspect its page count, and open or download the result.'],
    ],
    limitsIntro: 'Merge is bounded to keep page copying predictable in a browser tab. The exact limit is shown before you select files.',
    disclosures: [
      ['What stays intact?', 'The page contents are copied into a new document. Forms, annotations, metadata, and signatures may not survive a rewrite.'],
      ['What is rejected?', 'Empty, damaged, protected, over-size, and zero-page PDFs are reported per file.'],
      ['Where is it processed?', 'The parser and writer run locally. No document upload or remote conversion endpoint is used.'],
    ],
    faq: [
      ['Can I change the order?', 'Yes. Every accepted document has Move up and Move down buttons, so sorting does not depend on dragging.'],
      ['Does this preserve signatures?', 'No promise is made that a new merged PDF keeps digital signatures valid.'],
    ],
  },
  split: {
    eyebrow: 'Kivoza document tools · PDF',
    title: 'Split a PDF by page or range.',
    lede: 'Choose every page or enter exact ranges, then download the separate PDFs in one ZIP. Everything happens locally.',
    intro: 'Add one PDF. Use a range such as 1-3, 5, 8-9, or split every page.',
    trust: ['Local processing', 'No upload', 'ZIP results'],
    workspaceEyebrow: 'Make smaller files',
    workspaceTitle: 'Choose your pages.',
    steps: [
      ['Add', 'Select one PDF. We show its page count and prepare page previews as you need them.'],
      ['Define', 'Use every page or enter a strict, checked range list.'],
      ['Download', 'Each non-empty range becomes a PDF inside a ZIP with stable page names.'],
    ],
    limitsIntro: 'Split accepts up to 200 pages per input and 400 pages across one output run. Range errors stop before any file is created.',
    disclosures: [
      ['How are names made?', 'Results use the source name plus the selected page span, for example report-pages-001-003.pdf.'],
      ['Can a range be empty?', 'No. Blank tokens, reversed ranges, zero pages, and pages beyond the document are rejected.'],
      ['Does split upload my file?', 'No. Reading, copying, ZIP creation, and downloads stay in this tab.'],
    ],
    faq: [
      ['What does every page do?', 'It creates one one-page PDF per source page and places all results in one ZIP.'],
      ['Can I download one result?', 'Yes. The result panel provides individual links as well as the ZIP download.'],
    ],
  },
  rotate: {
    eyebrow: 'Kivoza document tools · PDF',
    title: 'Rotate selected PDF pages.',
    lede: 'Pick pages and apply a 90, 180, or 270 degree clockwise turn. The new PDF is generated in this browser tab.',
    intro: 'Add one PDF, select pages, and choose the angle. Existing page rotation is included in the result.',
    trust: ['Local processing', 'No upload', '90° steps'],
    workspaceEyebrow: 'Set page direction',
    workspaceTitle: 'Choose the pages.',
    steps: [
      ['Add', 'Select one PDF and review its page count, dimensions, and current rotation.'],
      ['Rotate', 'Select one, several, a range, or all pages. The angle is added clockwise.'],
      ['Download', 'The output keeps page order and leaves unselected page rotations unchanged.'],
    ],
    limitsIntro: 'Rotation changes page dictionaries in a new saved PDF. Rendered previews and saved page metadata are checked after export.',
    disclosures: [
      ['What does clockwise mean?', 'The chosen angle is added to each selected page rotation, modulo 360 degrees.'],
      ['What happens to signatures?', 'Rewriting a PDF may invalidate digital signatures. This tool does not claim signature preservation.'],
      ['Why can a page look unchanged?', 'Some PDFs draw content with their own transforms. The saved page rotation is still reported explicitly.'],
    ],
    faq: [
      ['Can I rotate all pages?', 'Use Select all, choose the angle, and export.'],
      ['Can I undo a rotation?', 'Start again from the original file and apply the opposite angle. Original bytes are never changed.'],
    ],
  },
  extract: {
    eyebrow: 'Kivoza document tools · PDF',
    title: 'Extract the PDF pages you need.',
    lede: 'Enter pages or ranges in the order you want, then create one focused PDF locally. No account or upload is needed.',
    intro: 'Add one PDF and enter pages like 4, 1-2. We reject empty, malformed, and out-of-range selections.',
    trust: ['Local processing', 'No upload', 'Pages in order'],
    workspaceEyebrow: 'Keep the useful pages',
    workspaceTitle: 'Select a sequence.',
    steps: [
      ['Add', 'Select one PDF and inspect its page count with on-demand previews.'],
      ['Select', 'Enter pages and ranges or use the page checkboxes to build an ordered list.'],
      ['Download', 'Create one PDF whose page order matches the selection you confirmed.'],
    ],
    limitsIntro: 'Extraction is limited to 200 input pages and 400 output pages. A blank selection never creates a zero-page file.',
    disclosures: [
      ['Can I change page order?', 'Yes. A range list such as 4, 1-2 is kept in that explicit order.'],
      ['Are duplicates allowed?', 'No. Duplicate page numbers are blocked so the result is predictable.'],
      ['Are original files changed?', 'No. The browser reads the input and creates a separate output Blob.'],
    ],
    faq: [
      ['What syntax is accepted?', 'Use comma-separated page numbers and inclusive ranges, such as 1, 3-5, 9.'],
      ['What if I type page 0?', 'The control reports that pages must be between 1 and the document page count.'],
    ],
  },
  'images-to-pdf': {
    eyebrow: 'Kivoza document tools · PDF',
    title: 'Turn JPG and PNG images into a PDF.',
    lede: 'Sort images, choose paper and margins, and create a clean PDF in your browser. Your images stay on your device.',
    intro: 'Add JPG or PNG images, set the page treatment, and check the order before export.',
    trust: ['Local processing', 'No upload', 'JPG + PNG'],
    workspaceEyebrow: 'Compose pages',
    workspaceTitle: 'Arrange your images.',
    steps: [
      ['Add', 'Select JPG or PNG files. Their signatures and dimensions are checked before decoding.'],
      ['Arrange', 'Use Move up and Move down to set the page order and choose paper treatment.'],
      ['Download', 'Each image becomes one fitted PDF page with a white page background.'],
    ],
    limitsIntro: 'The image route caps file count, bytes, decoded pixels, and output pages to keep browser memory bounded.',
    disclosures: [
      ['Which formats work?', 'Static JPEG and PNG images work. Animated or unsupported image formats are rejected.'],
      ['What does PNG transparency do?', 'The page background is white, so transparent pixels are composited onto white in the output.'],
      ['Are image details preserved?', 'The output is a new PDF. EXIF, color profiles, original compression, and metadata are not promised.'],
    ],
    faq: [
      ['Can I choose A4 or Letter?', 'Yes. Choose A4, Letter, or Original, then set portrait, landscape, or automatic orientation.'],
      ['Will images be cropped?', 'No. Images are fitted inside the selected page margins while preserving their aspect ratio.'],
    ],
  },
};

const state = {
  pdfSource: null,
  mergeEntries: [],
  imageEntries: [],
  imageSettings: {
    paper: 'a4',
    orientation: 'auto',
    margin: '12',
  },
  selectedPages: new Set(),
  selectedOrder: [],
  rangeText: '',
  rotationRangeText: '',
  splitMode: 'every',
  rotationAngle: 90,
  output: null,
  status: '',
  statusKind: '',
  working: false,
  runId: 0,
  pdfLibPromise: null,
  pdfJsPromise: null,
  zipPromise: null,
  thumbObserver: null,
};

function getConfig() {
  return TOOL_CONFIG[route] || null;
}

function normalizeAngle(angle) {
  return ((Number(angle) || 0) % 360 + 360) % 360;
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatPoints(value) {
  return `${Math.round(value)} pt`;
}

function makeId(prefix = 'item') {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(16).slice(2)}`;
}

function setStatus(message, kind = '') {
  state.status = message;
  state.statusKind = kind;
  const node = document.querySelector('#status-message');
  if (node) {
    node.textContent = message;
    node.className = `status-message${kind ? ` ${kind}` : ''}`;
  }
}

function revokeOutputUrls(output) {
  if (!output) return;
  const urls = [];
  if (output.url) urls.push(output.url);
  if (output.zipUrl) urls.push(output.zipUrl);
  if (output.items) output.items.forEach((item) => { if (item.url) urls.push(item.url); });
  urls.forEach((url) => URL.revokeObjectURL(url));
}

function clearOutput() {
  if (state.output) revokeOutputUrls(state.output);
  state.output = null;
  document.querySelectorAll('.result-panel').forEach((node) => node.remove());
}

function disposePdfEntry(entry) {
  if (!entry) return;
  entry.disposed = true;
  if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
  const thumbnailPdf = entry.thumbnailPdf;
  const thumbnailLoadingTask = entry.thumbnailLoadingTask;
  if (thumbnailPdf && typeof thumbnailPdf.cleanup === 'function') {
    try {
      const cleanup = thumbnailPdf.cleanup();
      if (cleanup && typeof cleanup.catch === 'function') cleanup.catch(() => {});
    } catch {
      // The preview may already have been torn down during a route change.
    }
  }
  if (thumbnailLoadingTask && typeof thumbnailLoadingTask.destroy === 'function') {
    try {
      const destroy = thumbnailLoadingTask.destroy();
      if (destroy && typeof destroy.catch === 'function') destroy.catch(() => {});
    } catch {
      // The loading task may have completed or failed before disposal.
    }
  }
  entry.previewUrl = null;
  entry.thumbnailPdf = null;
  entry.thumbnailLoadingTask = null;
}

function disposeImageEntry(entry) {
  if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);
}

function resetToolState() {
  state.runId += 1;
  state.working = false;
  clearOutput();
  disposePdfEntry(state.pdfSource);
  state.mergeEntries.forEach(disposePdfEntry);
  state.imageEntries.forEach(disposeImageEntry);
  state.pdfSource = null;
  state.mergeEntries = [];
  state.imageEntries = [];
  state.imageSettings = { paper: 'a4', orientation: 'auto', margin: '12' };
  state.selectedPages = new Set();
  state.selectedOrder = [];
  state.rangeText = '';
  state.rotationRangeText = '';
  state.status = '';
  state.statusKind = '';
  if (state.thumbObserver) state.thumbObserver.disconnect();
  state.thumbObserver = null;
}

async function loadPdfLib() {
  if (!state.pdfLibPromise) {
    setStatus('Loading the local PDF engine…');
    state.pdfLibPromise = import('@cantoo/pdf-lib');
  }
  return state.pdfLibPromise;
}

async function loadPdfJs() {
  if (!state.pdfJsPromise) {
    setStatus('Loading the local preview engine…');
    state.pdfJsPromise = import('pdfjs-dist').then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();
      return pdfjs;
    });
  }
  return state.pdfJsPromise;
}

async function loadZip() {
  if (!state.zipPromise) state.zipPromise = import('jszip').then((module) => module.default || module);
  return state.zipPromise;
}

function ascii(bytes, start, length) {
  return String.fromCharCode(...bytes.slice(start, start + length));
}

function hasPdfHeader(bytes) {
  return bytes.length >= 5 && ascii(bytes, 0, 5) === '%PDF-';
}

function hasPdfEof(bytes) {
  const start = Math.max(0, bytes.length - 4096);
  return ascii(bytes, start, bytes.length - start).includes('%%EOF');
}

function pdfError(message) {
  const error = new Error(message);
  error.name = 'KivozaPdfError';
  return error;
}

async function readFileBytes(file, maxBytes, label = 'file') {
  if (!file || file.size === 0) throw new Error(`The ${label} is empty.`);
  if (file.size > maxBytes) throw new Error(`The ${label} is larger than ${formatBytes(maxBytes)}.`);
  return new Uint8Array(await file.arrayBuffer());
}

function describePdfError(error) {
  const text = String(error?.message || error || '');
  if (/encrypt|password|protected/i.test(text)) return 'Password-protected PDFs are not supported. The file was not decrypted.';
  if (/invalid|parse|header|trailer|xref|eof|PDF/i.test(text)) return 'The PDF is damaged or uses a feature this browser tool cannot read.';
  return 'The PDF could not be read. Try a different file or open it in a desktop PDF reader first.';
}

async function inspectPdfFile(file) {
  const bytes = await readFileBytes(file, MAX_PDF_BYTES, 'PDF');
  if (!hasPdfHeader(bytes)) throw pdfError('The file does not start with a PDF header. Check the file contents, not only its extension.');
  if (!hasPdfEof(bytes)) throw pdfError('The PDF has no usable end marker and may be truncated.');

  const { PDFDocument } = await loadPdfLib();
  let probe;
  try {
    // The non-strict probe lets Cantoo expose `isEncrypted` for AES-256 files whose
    // encrypted object streams cannot be inspected without a password. We reject
    // immediately and never read pages from an encrypted document. Unencrypted
    // inputs are then loaded again in strict mode for corruption checks.
    probe = await PDFDocument.load(bytes, { ignoreEncryption: true, throwOnInvalidObject: false });
  } catch (error) {
    throw pdfError(describePdfError(error));
  }
  if (probe.isEncrypted) throw pdfError('Password-protected PDFs are not supported. The file was not decrypted.');
  let doc;
  try {
    doc = await PDFDocument.load(bytes, { throwOnInvalidObject: true });
  } catch (error) {
    throw pdfError(describePdfError(error));
  }
  if (doc.isEncrypted) throw pdfError('Password-protected PDFs are not supported. The file was not decrypted.');
  const pages = doc.getPages();
  if (!pages.length) throw pdfError('This PDF has no pages.');
  if (pages.length > MAX_PDF_PAGES) throw pdfError(`This PDF has more than ${MAX_PDF_PAGES} pages.`);
  const pageMeta = pages.map((page, index) => {
    const { width, height } = page.getSize();
    return { index, width, height, rotation: normalizeAngle(page.getRotation().angle) };
  });
  return { file, bytes, pageMeta, pageCount: pages.length };
}

async function validatePdfOutput(bytes, expectedPages, expectedRotations = null, expectedSizes = null) {
  if (!hasPdfHeader(bytes) || !hasPdfEof(bytes)) throw new Error('The generated file did not pass the PDF signature check.');
  const { PDFDocument } = await loadPdfLib();
  const doc = await PDFDocument.load(bytes, { throwOnInvalidObject: true });
  if (doc.isEncrypted) throw new Error('The generated file was unexpectedly encrypted.');
  if (doc.getPageCount() !== expectedPages) {
    throw new Error(`The generated PDF has ${doc.getPageCount()} pages; expected ${expectedPages}.`);
  }
  const pages = doc.getPages();
  if (expectedRotations) {
    pages.forEach((page, index) => {
      if (normalizeAngle(page.getRotation().angle) !== normalizeAngle(expectedRotations[index])) {
        throw new Error(`The generated rotation on page ${index + 1} did not match the requested angle.`);
      }
    });
  }
  if (expectedSizes) {
    pages.forEach((page, index) => {
      const actual = page.getSize();
      const expected = expectedSizes[index];
      if (!expected || Math.abs(actual.width - expected.width) > 0.01 || Math.abs(actual.height - expected.height) > 0.01) {
        throw new Error(`The generated page order or dimensions did not match page ${index + 1}.`);
      }
    });
  }
  return doc;
}

function createPdfEntry(file, inspection = null, error = '') {
  return {
    id: makeId('pdf'),
    file,
    bytes: inspection?.bytes || null,
    pageMeta: inspection?.pageMeta || [],
    pageCount: inspection?.pageCount || 0,
    error,
    status: error ? 'error' : 'ready',
    previewUrl: null,
    thumbnailPdf: null,
    thumbnailLoadingTask: null,
    disposed: false,
  };
}

function createImageEntry(file, data = null, error = '') {
  return {
    id: makeId('image'),
    file,
    bytes: data?.bytes || null,
    width: data?.width || 0,
    height: data?.height || 0,
    mime: data?.mime || '',
    error,
    status: error ? 'error' : 'ready',
    previewUrl: data?.previewUrl || null,
  };
}

function makeElement(tag, className = '', text = '') {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function button(label, className = 'mini-button', handler = null, disabled = false) {
  const node = makeElement('button', className, label);
  node.type = 'button';
  node.disabled = disabled;
  if (handler) node.addEventListener('click', handler);
  return node;
}

function makeHeader() {
  const header = makeElement('header', 'site-header');
  const inner = makeElement('div', 'header-inner');
  const brand = makeElement('a', 'brand');
  brand.href = 'https://kivoza.top/';
  brand.setAttribute('aria-label', 'Kivoza home');
  const mark = makeElement('span', 'brand-mark', 'K');
  mark.setAttribute('aria-hidden', 'true');
  brand.append(mark, makeElement('span', '', 'Kivoza'));
  const nav = makeElement('nav', 'header-nav');
  nav.setAttribute('aria-label', 'PDF tools');
  const links = [
    ['Merge', 'merge'],
    ['Split', 'split'],
    ['Rotate', 'rotate'],
    ['Extract', 'extract'],
    ['Images to PDF', 'images-to-pdf'],
  ];
  links.forEach(([label, value]) => {
    const link = makeElement('a', 'pdf-nav-link', label);
    link.href = `/${value}/`;
    if (route === value) link.setAttribute('aria-current', 'page');
    nav.append(link);
  });
  const open = makeElement('a', 'nav-button', route === 'home' ? 'Open merger' : 'Open tool');
  open.href = route === 'home' ? '/merge/' : '#workspace';
  nav.append(open);
  inner.append(brand, nav);
  header.append(inner);
  return header;
}

function makeTrustRow(items) {
  const row = makeElement('div', 'trust-row');
  row.setAttribute('aria-label', 'Processing details');
  items.forEach((item, index) => {
    const pill = makeElement('span', 'trust-item', item);
    if (index === 0) {
      const dot = makeElement('span', 'status-dot');
      dot.setAttribute('aria-hidden', 'true');
      pill.prepend(dot);
    }
    row.append(pill);
  });
  return row;
}

function makeUploadZone({ multiple = false, accept = '.pdf,application/pdf', title, help, limit, onFiles }) {
  const zone = makeElement('div', 'drop-zone');
  zone.id = 'drop-zone';
  zone.tabIndex = 0;
  zone.setAttribute('role', 'button');
  zone.setAttribute('aria-controls', 'file-input');
  zone.setAttribute('aria-describedby', 'drop-help');
  const input = document.createElement('input');
  input.id = 'file-input';
  input.className = 'visually-hidden';
  input.type = 'file';
  input.accept = accept;
  input.multiple = multiple;
  const icon = makeElement('div', 'drop-icon', '+');
  icon.setAttribute('aria-hidden', 'true');
  zone.append(input, icon, makeElement('h3', '', title), makeElement('p', '', help));
  const limitNode = makeElement('p', 'drop-limit', limit);
  limitNode.id = 'drop-help';
  zone.append(limitNode);
  const choose = () => input.click();
  zone.addEventListener('click', (event) => { if (event.target !== input) choose(); });
  zone.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); choose(); }
  });
  input.addEventListener('change', () => { onFiles(input.files); input.value = ''; });
  zone.addEventListener('dragover', (event) => { event.preventDefault(); zone.classList.add('is-dragging'); });
  zone.addEventListener('dragleave', () => zone.classList.remove('is-dragging'));
  zone.addEventListener('drop', (event) => {
    event.preventDefault();
    zone.classList.remove('is-dragging');
    onFiles(event.dataTransfer.files);
  });
  return zone;
}

function makeHero(config, workspace) {
  const section = makeElement('section', 'tool-hero');
  section.setAttribute('aria-labelledby', 'hero-title');
  const copy = makeElement('div', 'hero-copy');
  copy.append(
    makeElement('p', 'eyebrow', config.eyebrow),
    makeElement('h1', '', config.title),
    makeElement('p', 'hero-lede', config.lede),
    makeTrustRow(config.trust),
  );
  copy.querySelector('h1').id = 'hero-title';
  section.append(copy, workspace);
  return section;
}

function makeInfoSections(config) {
  const fragment = document.createDocumentFragment();
  const info = makeElement('section', 'info-section');
  info.id = 'how-it-works';
  info.setAttribute('aria-labelledby', 'how-title');
  const heading = makeElement('div', 'section-heading compact-heading');
  const headingWrap = document.createElement('div');
  headingWrap.append(makeElement('p', 'eyebrow', 'A short path'), makeElement('h2', '', `${config.steps[0][0]}, ${config.steps[1][0].toLowerCase()}, ${config.steps[2][0].toLowerCase()}.`));
  headingWrap.querySelector('h2').id = 'how-title';
  heading.append(headingWrap);
  info.append(heading);
  const steps = makeElement('div', 'steps-grid');
  config.steps.forEach(([title, text], index) => {
    const item = makeElement('article', 'step-item');
    const number = makeElement('span', 'step-number', `0${index + 1}`);
    item.append(number, makeElement('h3', '', title), makeElement('p', '', text));
    steps.append(item);
  });
  info.append(steps);

  const limits = makeElement('section', 'limits-section');
  limits.id = 'limits';
  limits.setAttribute('aria-labelledby', 'limits-title');
  const limitsHeading = makeElement('div', 'section-heading compact-heading');
  const limitsWrap = document.createElement('div');
  limitsWrap.append(makeElement('p', 'eyebrow', 'Know the edges'), makeElement('h2', '', 'Bounded for a browser tab.'));
  limitsWrap.querySelector('h2').id = 'limits-title';
  limitsHeading.append(limitsWrap, makeElement('p', 'section-intro', config.limitsIntro));
  limits.append(limitsHeading);
  const cards = makeElement('div', 'limits-grid');
  const limitValues = route === 'images-to-pdf'
    ? [['20', 'images per batch'], ['100 MB', 'total input bytes'], ['240 MP', 'candidate decoded batch cap']]
    : route === 'merge'
      ? [['8', 'PDF inputs'], ['200', 'pages per input'], ['400', 'candidate output pages']]
      : [['50 MB', 'PDF input limit'], ['200', 'pages per input'], ['120 px', 'thumbnail width']];
  limitValues.forEach(([value, text]) => {
    const card = makeElement('div', 'limit-card');
    card.append(makeElement('strong', '', value), makeElement('span', '', text));
    cards.append(card);
  });
  limits.append(cards);
  const disclosure = makeElement('div', 'disclosure-grid');
  config.disclosures.forEach(([title, text]) => {
    const item = document.createElement('div');
    item.append(makeElement('h3', '', title), makeElement('p', '', text));
    disclosure.append(item);
  });
  limits.append(disclosure);

  const faq = makeElement('section', 'faq-section');
  faq.setAttribute('aria-labelledby', 'faq-title');
  const faqHeading = makeElement('div', 'section-heading compact-heading');
  const faqTitle = makeElement('h2', '', 'Frequently asked questions');
  faqTitle.id = 'faq-title';
  faqHeading.append(faqTitle);
  faq.append(faqHeading);
  const faqList = makeElement('div', 'faq-list');
  config.faq.forEach(([question, answer]) => {
    const details = document.createElement('details');
    details.append(makeElement('summary', '', question), makeElement('p', '', answer));
    faqList.append(details);
  });
  faq.append(faqList);

  fragment.append(info, limits, faq);
  return fragment;
}

function makeFooter() {
  const footer = makeElement('footer', 'site-footer');
  const inner = makeElement('div', 'footer-inner');
  const identity = document.createElement('div');
  const brand = makeElement('a', 'brand');
  brand.href = 'https://kivoza.top/';
  brand.setAttribute('aria-label', 'Kivoza home');
  const mark = makeElement('span', 'brand-mark', 'K');
  mark.setAttribute('aria-hidden', 'true');
  brand.append(mark, makeElement('span', '', 'Kivoza'));
  identity.append(brand, makeElement('p', '', 'Small browser tools with clear boundaries.'));
  const nav = document.createElement('nav');
  nav.setAttribute('aria-label', 'Footer navigation');
  [['About', 'https://kivoza.top/about/'], ['Privacy', 'https://kivoza.top/privacy/'], ['Terms', 'https://kivoza.top/terms/'], ['Contact', 'https://kivoza.top/contact/']].forEach(([label, href]) => {
    const link = makeElement('a', '', label);
    link.href = href;
    nav.append(link);
  });
  inner.append(identity, nav, makeElement('p', 'footer-note', 'No file upload · No account required'));
  const copyright = makeElement('p', 'copyright', `© ${new Date().getFullYear()} Kivoza`);
  footer.append(inner, copyright);
  return footer;
}

function buildWorkspace(config) {
  const card = makeElement('div', 'converter-card');
  card.id = 'workspace';
  card.setAttribute('aria-labelledby', 'workspace-title');
  const heading = makeElement('div', 'converter-heading');
  const titleWrap = document.createElement('div');
  titleWrap.append(makeElement('p', 'eyebrow', config.workspaceEyebrow), makeElement('h2', '', config.workspaceTitle));
  titleWrap.querySelector('h2').id = 'workspace-title';
  heading.append(titleWrap, makeElement('p', 'converter-intro', config.intro));
  card.append(heading);
  if (route === 'merge') buildMergeWorkspace(card);
  else if (route === 'split') buildSplitWorkspace(card);
  else if (route === 'rotate') buildRotateWorkspace(card);
  else if (route === 'extract') buildExtractWorkspace(card);
  else if (route === 'images-to-pdf') buildImagesWorkspace(card);
  return card;
}

function appendStatus(card) {
  const status = makeElement('p', `status-message${state.statusKind ? ` ${state.statusKind}` : ''}`, state.status);
  status.id = 'status-message';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  card.append(status);
}

function appendResult(card) {
  if (!state.output) return;
  const panel = makeElement('section', 'result-panel');
  panel.setAttribute('aria-labelledby', 'result-title');
  const summary = document.createElement('div');
  const title = makeElement('h3', '', 'Ready to download');
  title.id = 'result-title';
  const outputCount = state.output.pageCount ? `${state.output.pageCount} pages` : state.output.items ? `${state.output.items.length} files` : '';
  summary.append(title, makeElement('p', 'muted-text', `${outputCount}${state.output.size ? ` · ${formatBytes(state.output.size)}` : ''}`));
  const actions = makeElement('div', 'result-actions');
  if (state.output.url) {
    const download = makeElement('a', 'button button-primary', state.output.downloadLabel || 'Download PDF');
    download.href = state.output.url;
    download.download = state.output.name;
    actions.append(download);
    const preview = makeElement('a', 'button button-secondary', 'Open preview');
    preview.href = state.output.url;
    preview.target = '_blank';
    preview.rel = 'noopener';
    actions.append(preview);
  }
  if (state.output.items) {
    const zip = makeElement('a', 'button button-primary', 'Download ZIP');
    zip.href = state.output.zipUrl;
    zip.download = state.output.zipName;
    actions.append(zip);
  }
  panel.append(summary, actions);
  if (state.output.items) {
    const list = makeElement('div', 'result-files');
    state.output.items.forEach((item) => {
      const row = makeElement('div', 'result-file');
      const details = document.createElement('div');
      const link = makeElement('a', '', item.name);
      link.href = item.url;
      link.target = '_blank';
      link.rel = 'noopener';
      const download = makeElement('a', '', 'Download');
      download.href = item.url;
      download.download = item.name;
      details.append(link, makeElement('span', 'queue-meta', ` · ${item.pageCount} ${item.pageCount === 1 ? 'page' : 'pages'} · ${formatBytes(item.size)}`));
      row.append(details, download);
      list.append(row);
    });
    panel.append(list);
  }
  card.append(panel);
}

function renderApp() {
  if (state.thumbObserver) state.thumbObserver.disconnect();
  const config = getConfig();
  const fragment = document.createDocumentFragment();
  fragment.append(makeHeader());
  if (!config) {
    fragment.append(makeHomeContent());
  } else {
    const workspace = buildWorkspace(config);
    fragment.append(makeHero(config, workspace), makeInfoSections(config));
  }
  fragment.append(makeFooter());
  app.replaceChildren(fragment);
  if (config) observeThumbnails();
}

function makeHomeContent() {
  const section = makeElement('section', 'tool-hero pdf-home-hero');
  section.setAttribute('aria-labelledby', 'hero-title');
  const copy = makeElement('div', 'hero-copy');
  copy.append(makeElement('p', 'eyebrow', 'Kivoza document tools · PDF'), makeElement('h1', '', 'Sort out a PDF locally.'), makeElement('p', 'hero-lede', 'Five focused browser tools for putting pages in order, changing their direction, and making PDFs from images.'), makeTrustRow(['Local processing', 'No upload', 'Five tools']));
  copy.querySelector('h1').id = 'hero-title';
  const grid = makeElement('div', 'tool-directory');
  Object.entries(TOOL_CONFIG).forEach(([key, config], index) => {
    const link = makeElement('a', 'tool-directory-item');
    link.href = `/${key}/`;
    const number = makeElement('span', 'step-number', `0${index + 1}`);
    link.append(number, makeElement('h2', '', config.title.replace(/\.$/, '')), makeElement('p', '', config.lede));
    grid.append(link);
  });
  section.append(copy, grid);
  const info = makeElement('section', 'info-section home-note');
  const inner = makeElement('div', 'section-heading compact-heading');
  inner.append(makeElement('h2', '', 'A clear boundary for every file.'));
  info.append(inner, makeElement('p', 'section-intro', 'PDFs and images are read, rendered, copied, and downloaded in this browser tab. Password-protected, damaged, and unsupported files are reported instead of being silently changed.'));
  const fragment = document.createDocumentFragment();
  fragment.append(section, info);
  return fragment;
}

function buildMergeWorkspace(card) {
  const zone = makeUploadZone({
    multiple: true,
    title: 'Drop PDF files here',
    help: 'or browse your device. File contents are checked before merge.',
    limit: `Up to ${MAX_PDF_FILES} PDFs · ${formatBytes(MAX_PDF_BYTES)} each · ${MAX_PDF_PAGES} pages each`,
    onFiles: (files) => addMergeFiles(files),
  });
  card.append(zone);
  const toolbar = makeElement('div', 'queue-toolbar');
  const left = document.createElement('div');
  left.append(makeElement('h3', '', `Document order ${state.mergeEntries.length ? `(${state.mergeEntries.length})` : ''}`), makeElement('p', 'muted-text', state.mergeEntries.length ? `${state.mergeEntries.filter((entry) => entry.status === 'ready').length} ready · move documents before merging` : 'Add documents to begin.'));
  const actions = makeElement('div', 'toolbar-actions');
  actions.append(button('Merge PDFs', 'button button-primary', () => mergePdfs(), !state.mergeEntries.some((entry) => entry.status === 'ready') || state.working), button('Clear', 'button button-secondary', () => { resetToolState(); renderApp(); setStatus('Queue cleared. Your files were never uploaded.'); }, !state.mergeEntries.length || state.working));
  toolbar.append(left, actions);
  card.append(toolbar);
  const list = makeElement('div', 'queue-list document-list');
  state.mergeEntries.forEach((entry, index) => list.append(buildMergeEntry(entry, index)));
  card.append(list);
  appendResult(card);
  appendStatus(card);
}

function buildMergeEntry(entry, index) {
  const item = makeElement('article', `queue-item document-item${entry.status === 'error' ? ' is-error' : ''}`);
  const thumb = makeElement('div', 'queue-thumb page-thumb-placeholder');
  if (entry.status === 'ready') {
    const canvas = document.createElement('canvas');
    canvas.className = 'page-thumbnail queue-page-thumbnail';
    canvas.width = 1;
    canvas.height = 1;
    canvas.setAttribute('aria-label', `Preview of the first page of ${entry.file.name}`);
    canvas.dataset.thumbEntry = entry.id;
    canvas.dataset.thumbPage = '0';
    thumb.append(canvas);
  } else {
    thumb.textContent = 'PDF';
    thumb.setAttribute('aria-hidden', 'true');
  }
  const details = document.createElement('div');
  const name = makeElement('div', 'queue-name', entry.file.name);
  name.title = entry.file.name;
  const metaText = entry.status === 'ready' ? `${formatBytes(entry.file.size)} · ${entry.pageCount} ${entry.pageCount === 1 ? 'page' : 'pages'}` : formatBytes(entry.file.size);
  details.append(name, makeElement('div', 'queue-meta', metaText), makeElement('div', `queue-status ${entry.status === 'error' ? 'error' : 'success'}`, entry.error || 'Ready to merge'));
  const actions = makeElement('div', 'queue-actions');
  if (index > 0) actions.append(button('Move up', 'mini-button', () => { [state.mergeEntries[index - 1], state.mergeEntries[index]] = [state.mergeEntries[index], state.mergeEntries[index - 1]]; renderApp(); }, state.working));
  if (index < state.mergeEntries.length - 1) actions.append(button('Move down', 'mini-button', () => { [state.mergeEntries[index + 1], state.mergeEntries[index]] = [state.mergeEntries[index], state.mergeEntries[index + 1]]; renderApp(); }, state.working));
  actions.append(button('Remove', 'mini-button', () => { disposePdfEntry(entry); state.mergeEntries.splice(index, 1); clearOutput(); renderApp(); setStatus(`${entry.file.name} removed from the queue.`); }, state.working));
  item.append(thumb, details, actions);
  return item;
}

async function addMergeFiles(fileList) {
  const incoming = [...fileList];
  if (!incoming.length || state.working) return;
  if (state.mergeEntries.length + incoming.length > MAX_PDF_FILES) {
    setStatus(`A merge can contain up to ${MAX_PDF_FILES} PDF files.`, 'error');
    return;
  }
  const currentBytes = state.mergeEntries.reduce((sum, entry) => sum + entry.file.size, 0);
  if (currentBytes + incoming.reduce((sum, file) => sum + file.size, 0) > MAX_PDF_BYTES * 2) {
    setStatus(`Keep the merge input below ${formatBytes(MAX_PDF_BYTES * 2)}.`, 'error');
    return;
  }
  state.working = true;
  const runId = ++state.runId;
  clearOutput();
  renderApp();
  for (const file of incoming) {
    if (runId !== state.runId) return;
    setStatus(`Checking ${file.name}…`);
    try {
      const inspection = await inspectPdfFile(file);
      if (runId !== state.runId) return;
      state.mergeEntries.push(createPdfEntry(file, inspection));
    } catch (error) {
      if (runId !== state.runId) return;
      state.mergeEntries.push(createPdfEntry(file, null, error.message || describePdfError(error)));
    }
    renderApp();
  }
  state.working = false;
  setStatus('Files checked. Review the order, then merge.');
  renderApp();
}

async function mergePdfs() {
  const entries = state.mergeEntries.filter((entry) => entry.status === 'ready');
  if (!entries.length || state.working) return;
  const totalPages = entries.reduce((sum, entry) => sum + entry.pageCount, 0);
  if (totalPages > MAX_OUTPUT_PAGES) { setStatus(`The merged output would exceed ${MAX_OUTPUT_PAGES} pages.`, 'error'); return; }
  state.working = true;
  const runId = ++state.runId;
  clearOutput();
  renderApp();
  try {
    const { PDFDocument } = await loadPdfLib();
    const outputDoc = await PDFDocument.create();
    for (const entry of entries) {
      if (runId !== state.runId) return;
      setStatus(`Copying ${entry.file.name}…`);
      const source = await PDFDocument.load(entry.bytes, { throwOnInvalidObject: true });
      if (source.isEncrypted) throw new Error('Password-protected PDFs are not supported. The file was not decrypted.');
      const pages = await outputDoc.copyPages(source, source.getPageIndices());
      pages.forEach((page) => outputDoc.addPage(page));
    }
    const bytes = await outputDoc.save();
    await validatePdfOutput(bytes, totalPages, null, entries.flatMap((entry) => entry.pageMeta));
    if (runId !== state.runId) return;
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    if (runId !== state.runId) {
      URL.revokeObjectURL(url);
      return;
    }
    state.output = { name: 'kivoza-merged.pdf', url, size: bytes.length, pageCount: totalPages };
    setStatus(`Merged ${entries.length} documents into ${totalPages} pages.`, 'success');
  } catch (error) {
    setStatus(error.message || 'The PDF could not be merged.', 'error');
  } finally {
    if (runId === state.runId) {
      state.working = false;
      renderApp();
    }
  }
}

function buildSinglePdfWorkspace(card, mode) {
  const zone = makeUploadZone({
    multiple: false,
    title: 'Drop one PDF here',
    help: 'or browse your device. Page information appears after validation.',
    limit: `${formatBytes(MAX_PDF_BYTES)} maximum · ${MAX_PDF_PAGES} pages maximum · protected files are rejected`,
    onFiles: (files) => addSinglePdf(files?.[0]),
  });
  card.append(zone);
  if (state.pdfSource) {
    const summary = makeElement('div', 'source-summary');
    summary.append(makeElement('strong', '', state.pdfSource.file.name), makeElement('span', 'queue-meta', `${formatBytes(state.pdfSource.file.size)} · ${state.pdfSource.pageCount} ${state.pdfSource.pageCount === 1 ? 'page' : 'pages'}`));
    if (state.pdfSource.error) summary.append(makeElement('span', 'queue-status error', state.pdfSource.error));
    const remove = button('Remove', 'mini-button', () => { disposePdfEntry(state.pdfSource); state.pdfSource = null; state.selectedPages = new Set(); state.selectedOrder = []; state.rotationRangeText = ''; clearOutput(); renderApp(); setStatus('PDF removed.'); }, state.working);
    summary.append(remove);
    card.append(summary);
    if (state.pdfSource.status === 'ready') {
      if (mode === 'split') buildSplitControls(card);
      if (mode === 'rotate') buildRotateControls(card);
      if (mode === 'extract') buildExtractControls(card);
    }
  }
  appendResult(card);
  appendStatus(card);
}

function buildSplitWorkspace(card) { buildSinglePdfWorkspace(card, 'split'); }
function buildRotateWorkspace(card) { buildSinglePdfWorkspace(card, 'rotate'); }
function buildExtractWorkspace(card) { buildSinglePdfWorkspace(card, 'extract'); }

function buildPageList(entry, { checkboxes = false } = {}) {
  const section = makeElement('section', 'page-list-section');
  const heading = makeElement('div', 'queue-toolbar page-list-heading');
  const titleWrap = document.createElement('div');
  titleWrap.append(makeElement('h3', '', `Pages (${entry.pageCount})`), makeElement('p', 'muted-text', 'Previews render as pages enter the viewport.'));
  heading.append(titleWrap);
  const list = makeElement('div', 'page-list');
  entry.pageMeta.forEach((meta, index) => list.append(buildPageItem(entry, meta, index, checkboxes)));
  section.append(heading, list);
  return section;
}

function buildPageItem(entry, meta, index, checkboxes) {
  const item = makeElement('article', 'page-item');
  item.dataset.pageIndex = String(index);
  const canvas = document.createElement('canvas');
  canvas.className = 'page-thumbnail';
  canvas.width = 1;
  canvas.height = 1;
  canvas.setAttribute('aria-label', `Preview of page ${index + 1}`);
  canvas.dataset.thumbEntry = entry.id;
  canvas.dataset.thumbPage = String(index);
  const info = makeElement('div', 'page-info');
  const label = makeElement('strong', '', `Page ${index + 1}`);
  const metaText = `${formatPoints(meta.width)} × ${formatPoints(meta.height)} · ${meta.rotation}°`;
  info.append(label, makeElement('span', 'queue-meta', metaText));
  if (checkboxes) {
    const labelWrap = makeElement('label', 'page-check');
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = state.selectedPages.has(index);
    input.disabled = state.working;
    input.setAttribute('aria-label', `Select page ${index + 1}`);
    input.addEventListener('change', () => {
      if (input.checked) {
        state.selectedPages.add(index);
        if (!state.selectedOrder.includes(index)) state.selectedOrder.push(index);
      } else {
        state.selectedPages.delete(index);
        state.selectedOrder = state.selectedOrder.filter((value) => value !== index);
      }
      if (route === 'rotate') state.rotationRangeText = '';
      renderApp();
    });
    labelWrap.append(input, makeElement('span', '', 'Select'));
    info.append(labelWrap);
  }
  item.append(canvas, info);
  return item;
}

async function addSinglePdf(file) {
  if (!file || state.working) return;
  state.working = true;
  const runId = ++state.runId;
  if (state.pdfSource) disposePdfEntry(state.pdfSource);
  clearOutput();
  state.pdfSource = null;
  state.selectedPages = new Set();
  state.selectedOrder = [];
  state.rotationRangeText = '';
  renderApp();
  setStatus(`Checking ${file.name}…`);
  try {
    const inspection = await inspectPdfFile(file);
    if (runId !== state.runId) return;
    state.pdfSource = createPdfEntry(file, inspection);
    state.rangeText = '';
    setStatus(`${file.name} is ready.`, 'success');
  } catch (error) {
    if (runId !== state.runId) return;
    state.pdfSource = createPdfEntry(file, null, error.message || describePdfError(error));
    setStatus(state.pdfSource.error, 'error');
  } finally {
    if (runId === state.runId) {
      state.working = false;
      renderApp();
    }
  }
}

function parseRangeGroups(text, pageCount, { allowDuplicates = false } = {}) {
  const value = String(text || '').trim();
  if (!value) throw new Error('Enter at least one page or range.');
  const groups = [];
  const seen = new Set();
  for (const raw of value.split(',')) {
    const token = raw.trim();
    if (!token) throw new Error('Remove the empty item between commas.');
    const match = /^(\d+)(?:\s*-\s*(\d+))?$/.exec(token);
    if (!match) throw new Error(`“${token}” is not a page number or range like 2-4.`);
    const start = Number(match[1]);
    const end = match[2] ? Number(match[2]) : start;
    if (start < 1 || end < 1 || start > pageCount || end > pageCount) throw new Error(`Pages must be between 1 and ${pageCount}.`);
    if (start > end) throw new Error(`Range ${token} is reversed. Use the smaller page first.`);
    const group = [];
    for (let page = start; page <= end; page += 1) {
      if (!allowDuplicates && seen.has(page)) throw new Error(`Page ${page} appears more than once.`);
      seen.add(page);
      group.push(page);
    }
    groups.push(group);
  }
  const pages = groups.flat();
  if (!pages.length) throw new Error('Choose at least one page.');
  if (pages.length > MAX_OUTPUT_PAGES) throw new Error(`Choose no more than ${MAX_OUTPUT_PAGES} output pages.`);
  return { groups, pages };
}

function makeRangeControl({ label, value = '', placeholder, onChange, disabled = false }) {
  const wrap = makeElement('label', 'range-control');
  wrap.append(makeElement('span', '', label));
  const input = document.createElement('input');
  input.type = 'text';
  input.value = value;
  input.placeholder = placeholder;
  input.autocomplete = 'off';
  input.disabled = disabled;
  input.addEventListener('input', () => onChange(input.value));
  wrap.append(input);
  return wrap;
}

function buildSplitControls(card) {
  const control = makeElement('div', 'operation-controls');
  const fieldset = document.createElement('fieldset');
  fieldset.className = 'format-fieldset';
  const legend = document.createElement('legend');
  legend.textContent = 'Split mode';
  fieldset.append(legend);
  [['every', 'Every page'], ['ranges', 'Page ranges']].forEach(([value, label]) => {
    const choice = makeElement('label', 'choice-pill');
    const input = document.createElement('input');
    input.type = 'radio'; input.name = 'split-mode'; input.value = value; input.checked = state.splitMode === value; input.disabled = state.working;
    input.addEventListener('change', () => { state.splitMode = value; clearOutput(); renderApp(); });
    choice.append(input, makeElement('span', '', label)); fieldset.append(choice);
  });
  control.append(fieldset);
  if (state.splitMode === 'ranges') control.append(makeRangeControl({ label: 'Ranges', value: state.rangeText, placeholder: '1-3, 5, 8-9', onChange: (value) => { state.rangeText = value; clearOutput(); }, disabled: state.working }));
  const actions = makeElement('div', 'operation-actions');
  actions.append(button('Split PDF', 'button button-primary', () => splitPdf(), state.working));
  control.append(actions);
  card.append(control, buildPageList(state.pdfSource));
}

function buildRotateControls(card) {
  const control = makeElement('div', 'operation-controls');
  const selectWrap = makeElement('label', 'select-control');
  selectWrap.append(makeElement('span', '', 'Clockwise angle'));
  const select = document.createElement('select');
  select.disabled = state.working;
  [90, 180, 270].forEach((angle) => { const option = document.createElement('option'); option.value = String(angle); option.textContent = `${angle}°`; option.selected = state.rotationAngle === angle; select.append(option); });
  select.addEventListener('change', () => { state.rotationAngle = Number(select.value); clearOutput(); });
  selectWrap.append(select);
  let rotateButton;
  const updateRotateButton = () => {
    if (rotateButton) rotateButton.disabled = state.working || (!state.selectedPages.size && !state.rotationRangeText.trim());
  };
  const range = makeRangeControl({ label: 'Pages or ranges (optional)', value: state.rotationRangeText, placeholder: '2, 4-6', onChange: (value) => { state.rotationRangeText = value; clearOutput(); updateRotateButton(); }, disabled: state.working });
  const selectActions = makeElement('div', 'selection-actions');
  selectActions.append(button('Select all', 'mini-button', () => { state.selectedPages = new Set(state.pdfSource.pageMeta.map((_, index) => index)); state.selectedOrder = [...state.selectedPages]; state.rotationRangeText = ''; renderApp(); }, state.working), button('Clear selection', 'mini-button', () => { state.selectedPages = new Set(); state.selectedOrder = []; state.rotationRangeText = ''; renderApp(); }, state.working));
  control.append(selectWrap, range, selectActions, makeElement('p', 'format-note', 'Use the optional range or checkboxes. The chosen angle is added to each selected page rotation.'));
  const actions = makeElement('div', 'operation-actions');
  rotateButton = button('Rotate selected pages', 'button button-primary', () => rotatePdf(), state.working || (!state.selectedPages.size && !state.rotationRangeText.trim()));
  actions.append(rotateButton);
  control.append(actions, buildPageList(state.pdfSource, { checkboxes: true }));
  card.append(control);
}

function buildExtractControls(card) {
  const control = makeElement('div', 'operation-controls');
  control.append(makeRangeControl({ label: 'Pages or ranges', value: state.rangeText, placeholder: '4, 1-2, 7', onChange: (value) => { state.rangeText = value; clearOutput(); }, disabled: state.working }));
  const selectActions = makeElement('div', 'selection-actions');
  selectActions.append(button('Use checked pages', 'mini-button', () => { state.rangeText = state.selectedOrder.map((index) => index + 1).join(', '); renderApp(); }, state.working), button('Select all', 'mini-button', () => { state.selectedPages = new Set(state.pdfSource.pageMeta.map((_, index) => index)); state.selectedOrder = [...state.selectedPages]; renderApp(); }, state.working));
  control.append(selectActions, makeElement('p', 'format-note', 'Typed ranges keep their order. Checkboxes provide a quick selection when order is not important.'));
  const actions = makeElement('div', 'operation-actions');
  actions.append(button('Extract pages', 'button button-primary', () => extractPdf(), state.working));
  control.append(actions, buildPageList(state.pdfSource, { checkboxes: true }));
  card.append(control);
}

async function splitPdf() {
  if (!state.pdfSource || state.pdfSource.status !== 'ready' || state.working) return;
  let groups;
  try {
    groups = state.splitMode === 'every'
      ? state.pdfSource.pageMeta.map((_, index) => [index + 1])
      : parseRangeGroups(state.rangeText, state.pdfSource.pageCount).groups;
    if (groups.flat().length > MAX_OUTPUT_PAGES) throw new Error(`Choose no more than ${MAX_OUTPUT_PAGES} output pages.`);
  } catch (error) {
    setStatus(error.message, 'error');
    return;
  }
  state.working = true;
  const runId = ++state.runId;
  clearOutput();
  renderApp();
  const items = [];
  let zipUrl = null;
  try {
    const { PDFDocument } = await loadPdfLib();
    const source = await PDFDocument.load(state.pdfSource.bytes, { throwOnInvalidObject: true });
    if (source.isEncrypted) throw new Error('Password-protected PDFs are not supported. The file was not decrypted.');
    const zip = await loadZip();
    const archive = new zip();
    const base = state.pdfSource.file.name.replace(/\.pdf$/i, '') || 'document';
    for (const group of groups) {
      if (runId !== state.runId) {
        revokeOutputUrls({ items });
        return;
      }
      const out = await PDFDocument.create();
      const pages = await out.copyPages(source, group.map((page) => page - 1));
      pages.forEach((page) => out.addPage(page));
      const bytes = await out.save();
      await validatePdfOutput(bytes, group.length, null, group.map((page) => state.pdfSource.pageMeta[page - 1]));
      const first = String(group[0]).padStart(3, '0');
      const last = String(group[group.length - 1]).padStart(3, '0');
      const name = `${base}-pages-${first}-${last}.pdf`;
      archive.file(name, bytes);
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      items.push({ name, pageCount: group.length, size: bytes.length, url });
      setStatus(`Created ${items.length} of ${groups.length} PDF files…`);
    }
    const zipBytes = await archive.generateAsync({ type: 'uint8array' });
    const zipName = `${base}-split.zip`;
    zipUrl = URL.createObjectURL(new Blob([zipBytes], { type: 'application/zip' }));
    if (runId !== state.runId) {
      revokeOutputUrls({ items, zipUrl });
      return;
    }
    state.output = { items, zipUrl, zipName, size: zipBytes.length };
    setStatus(`Created ${items.length} PDF files in a ZIP.`, 'success');
  } catch (error) {
    revokeOutputUrls({ items, zipUrl });
    setStatus(error.message || 'The PDF could not be split.', 'error');
  } finally {
    if (runId === state.runId) {
      state.working = false;
      renderApp();
    }
  }
}

async function rotatePdf() {
  if (!state.pdfSource || state.pdfSource.status !== 'ready' || state.working) return;
  let selectedPages;
  try {
    selectedPages = state.rotationRangeText.trim()
      ? new Set(parseRangeGroups(state.rotationRangeText, state.pdfSource.pageCount).pages.map((page) => page - 1))
      : new Set(state.selectedPages);
    if (!selectedPages.size) throw new Error('Select at least one page or enter a range.');
  } catch (error) {
    setStatus(error.message, 'error');
    return;
  }
  state.working = true;
  const runId = ++state.runId;
  clearOutput();
  renderApp();
  try {
    const { PDFDocument, degrees } = await loadPdfLib();
    const output = await PDFDocument.load(state.pdfSource.bytes, { throwOnInvalidObject: true });
    if (output.isEncrypted) throw new Error('Password-protected PDFs are not supported. The file was not decrypted.');
    const expected = [];
    output.getPages().forEach((page, index) => {
      const current = normalizeAngle(page.getRotation().angle);
      const next = selectedPages.has(index) ? normalizeAngle(current + state.rotationAngle) : current;
      expected.push(next);
      if (selectedPages.has(index)) page.setRotation(degrees(next));
    });
    if (runId !== state.runId) return;
    const bytes = await output.save();
    await validatePdfOutput(bytes, state.pdfSource.pageCount, expected, state.pdfSource.pageMeta);
    if (runId !== state.runId) return;
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    if (runId !== state.runId) {
      URL.revokeObjectURL(url);
      return;
    }
    state.output = { name: `${state.pdfSource.file.name.replace(/\.pdf$/i, '') || 'document'}-rotated.pdf`, url, size: bytes.length, pageCount: state.pdfSource.pageCount };
    setStatus(`Rotated ${selectedPages.size} selected ${selectedPages.size === 1 ? 'page' : 'pages'}.`, 'success');
  } catch (error) {
    setStatus(error.message || 'The PDF could not be rotated.', 'error');
  } finally {
    if (runId === state.runId) {
      state.working = false;
      renderApp();
    }
  }
}

async function extractPdf() {
  if (!state.pdfSource || state.pdfSource.status !== 'ready' || state.working) return;
  let pages;
  try {
    pages = state.rangeText.trim()
      ? parseRangeGroups(state.rangeText, state.pdfSource.pageCount).pages
      : state.selectedOrder.map((index) => index + 1);
    if (!pages.length) throw new Error('Select at least one page or enter a range.');
  } catch (error) {
    setStatus(error.message, 'error');
    return;
  }
  state.working = true;
  const runId = ++state.runId;
  clearOutput();
  renderApp();
  try {
    const { PDFDocument } = await loadPdfLib();
    const source = await PDFDocument.load(state.pdfSource.bytes, { throwOnInvalidObject: true });
    if (source.isEncrypted) throw new Error('Password-protected PDFs are not supported. The file was not decrypted.');
    const output = await PDFDocument.create();
    const copied = await output.copyPages(source, pages.map((page) => page - 1));
    copied.forEach((page) => output.addPage(page));
    if (runId !== state.runId) return;
    const bytes = await output.save();
    await validatePdfOutput(bytes, pages.length, null, pages.map((page) => state.pdfSource.pageMeta[page - 1]));
    if (runId !== state.runId) return;
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    if (runId !== state.runId) {
      URL.revokeObjectURL(url);
      return;
    }
    state.output = { name: `${state.pdfSource.file.name.replace(/\.pdf$/i, '') || 'document'}-pages.pdf`, url, size: bytes.length, pageCount: pages.length };
    setStatus(`Extracted ${pages.length} ${pages.length === 1 ? 'page' : 'pages'} in the requested order.`, 'success');
  } catch (error) {
    setStatus(error.message || 'The pages could not be extracted.', 'error');
  } finally {
    if (runId === state.runId) {
      state.working = false;
      renderApp();
    }
  }
}

function imageMimeFromBytes(bytes) {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
  if (bytes.length >= 8 && ascii(bytes, 0, 8) === '\x89PNG\r\n\x1a\n') return 'image/png';
  return '';
}

function readUint32(bytes, offset) {
  return (((bytes[offset] * 0x1000000) + (bytes[offset + 1] << 16) + (bytes[offset + 2] << 8) + bytes[offset + 3]) >>> 0);
}

function readImageDimensions(bytes, mime) {
  if (mime === 'image/png') {
    if (bytes.length < 24 || ascii(bytes, 12, 4) !== 'IHDR') throw new Error('The PNG header is incomplete.');
    const width = readUint32(bytes, 16);
    const height = readUint32(bytes, 20);
    if (!width || !height) throw new Error('The image has no usable dimensions.');
    return { width, height };
  }
  let offset = 2;
  while (offset + 3 < bytes.length) {
    while (offset < bytes.length && bytes[offset] === 0xff) offset += 1;
    if (offset >= bytes.length) break;
    const marker = bytes[offset];
    offset += 1;
    if (marker === 0xd8 || marker === 0xd9) continue;
    if (marker === 0xda || offset + 1 >= bytes.length) break;
    const length = (bytes[offset] << 8) | bytes[offset + 1];
    if (length < 2 || offset + length > bytes.length) break;
    const isFrame = marker >= 0xc0 && marker <= 0xcf && ![0xc4, 0xc8, 0xcc].includes(marker);
    if (isFrame && length >= 7) {
      const height = (bytes[offset + 3] << 8) | bytes[offset + 4];
      const width = (bytes[offset + 5] << 8) | bytes[offset + 6];
      if (!width || !height) throw new Error('The image has no usable dimensions.');
      return { width, height };
    }
    offset += length;
  }
  throw new Error('The JPEG header is incomplete.');
}

async function inspectImageFile(file) {
  const bytes = await readFileBytes(file, MAX_IMAGE_BYTES, 'image');
  const mime = imageMimeFromBytes(bytes);
  if (!mime) throw new Error('This file is not a supported JPG or PNG. The file contents were checked.');
  const headerDimensions = readImageDimensions(bytes, mime);
  if (headerDimensions.width > MAX_IMAGE_PIXELS / headerDimensions.height) throw new Error(`The image is larger than ${MAX_IMAGE_PIXELS / 1000000} megapixels.`);
  const previewUrl = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = previewUrl;
    await image.decode();
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    if (!width || !height) throw new Error('The image has no usable dimensions.');
    const pixels = width * height;
    if (pixels > MAX_IMAGE_PIXELS) throw new Error(`The image is larger than ${MAX_IMAGE_PIXELS / 1000000} megapixels.`);
    if (width !== headerDimensions.width || height !== headerDimensions.height) throw new Error('The image dimensions changed while decoding.');
    return { bytes, mime, width, height, previewUrl, pixels };
  } catch (error) {
    URL.revokeObjectURL(previewUrl);
    throw new Error(error.message || 'The image could not be decoded.');
  }
}

function buildImagesWorkspace(card) {
  const zone = makeUploadZone({
    multiple: true,
    accept: '.jpg,.jpeg,.png,image/jpeg,image/png',
    title: 'Drop JPG or PNG images here',
    help: 'or browse your device. Contents and dimensions are checked before export.',
    limit: `${MAX_IMAGE_FILES} images · ${formatBytes(MAX_IMAGE_BYTES)} each · ${formatBytes(MAX_IMAGE_TOTAL_BYTES)} per batch`,
    onFiles: (files) => addImageFiles(files),
  });
  card.append(zone);
  if (state.imageEntries.length) {
    const settings = makeElement('div', 'image-settings operation-controls');
    settings.append(makeSelectControl('Paper', 'paper-select', [['a4', 'A4'], ['letter', 'Letter'], ['original', 'Original size']], state.imageSettings.paper), makeSelectControl('Orientation', 'orientation-select', [['auto', 'Auto'], ['portrait', 'Portrait'], ['landscape', 'Landscape']], state.imageSettings.orientation), makeSelectControl('Margin', 'margin-select', [['0', '0 pt'], ['12', '12 pt'], ['24', '24 pt']], state.imageSettings.margin), makeElement('p', 'format-note', 'PNG transparency is placed on a white page. Images are fitted inside the margin without cropping.'));
    const settingKeys = { 'paper-select': 'paper', 'orientation-select': 'orientation', 'margin-select': 'margin' };
    settings.querySelectorAll('select').forEach((select) => {
      select.disabled = state.working;
      select.addEventListener('change', () => {
        state.imageSettings[settingKeys[select.id]] = select.value;
        clearOutput();
      });
    });
    card.append(settings);
    const toolbar = makeElement('div', 'queue-toolbar');
    const label = document.createElement('div');
    const ready = state.imageEntries.filter((entry) => entry.status === 'ready').length;
    label.append(makeElement('h3', '', `Image order (${state.imageEntries.length})`), makeElement('p', 'muted-text', `${ready} ready · one image per PDF page`));
    const actions = makeElement('div', 'toolbar-actions');
    actions.append(button('Create PDF', 'button button-primary', () => imagesToPdf(), state.working || !ready), button('Clear', 'button button-secondary', () => { resetToolState(); renderApp(); setStatus('Images cleared. Your originals were never uploaded.'); }, state.working));
    toolbar.append(label, actions);
    card.append(toolbar);
    const list = makeElement('div', 'queue-list image-list');
    state.imageEntries.forEach((entry, index) => list.append(buildImageEntry(entry, index)));
    card.append(list);
  }
  appendResult(card);
  appendStatus(card);
}

function makeSelectControl(label, id, options, defaultValue) {
  const wrap = makeElement('label', 'select-control');
  wrap.append(makeElement('span', '', label));
  const select = document.createElement('select');
  select.id = id;
  options.forEach(([value, text]) => { const option = document.createElement('option'); option.value = value; option.textContent = text; option.selected = value === defaultValue; select.append(option); });
  wrap.append(select);
  return wrap;
}

function buildImageEntry(entry, index) {
  const item = makeElement('article', `queue-item image-item${entry.status === 'error' ? ' is-error' : ''}`);
  const thumb = makeElement('div', 'queue-thumb');
  if (entry.previewUrl) { const image = document.createElement('img'); image.src = entry.previewUrl; image.alt = `Preview of ${entry.file.name}`; thumb.append(image); }
  else thumb.textContent = 'IMAGE';
  const details = document.createElement('div');
  details.append(makeElement('div', 'queue-name', entry.file.name), makeElement('div', 'queue-meta', entry.status === 'ready' ? `${formatBytes(entry.file.size)} · ${entry.width} × ${entry.height}` : formatBytes(entry.file.size)), makeElement('div', `queue-status ${entry.status === 'error' ? 'error' : 'success'}`, entry.error || 'Ready for a page'));
  const actions = makeElement('div', 'queue-actions');
  if (index > 0) actions.append(button('Move up', 'mini-button', () => { [state.imageEntries[index - 1], state.imageEntries[index]] = [state.imageEntries[index], state.imageEntries[index - 1]]; renderApp(); }, state.working));
  if (index < state.imageEntries.length - 1) actions.append(button('Move down', 'mini-button', () => { [state.imageEntries[index + 1], state.imageEntries[index]] = [state.imageEntries[index], state.imageEntries[index + 1]]; renderApp(); }, state.working));
  actions.append(button('Remove', 'mini-button', () => { disposeImageEntry(entry); state.imageEntries.splice(index, 1); clearOutput(); renderApp(); setStatus(`${entry.file.name} removed.`); }, state.working));
  item.append(thumb, details, actions);
  return item;
}

async function addImageFiles(fileList) {
  const incoming = [...fileList];
  if (!incoming.length || state.working) return;
  if (state.imageEntries.length + incoming.length > MAX_IMAGE_FILES) { setStatus(`A batch can contain up to ${MAX_IMAGE_FILES} images.`, 'error'); return; }
  const currentBytes = state.imageEntries.reduce((sum, entry) => sum + entry.file.size, 0);
  if (currentBytes + incoming.reduce((sum, file) => sum + file.size, 0) > MAX_IMAGE_TOTAL_BYTES) { setStatus(`Keep the image batch below ${formatBytes(MAX_IMAGE_TOTAL_BYTES)}.`, 'error'); return; }
  state.working = true;
  const runId = ++state.runId;
  clearOutput();
  renderApp();
  let totalPixels = state.imageEntries.reduce((sum, entry) => sum + entry.width * entry.height, 0);
  for (const file of incoming) {
    if (runId !== state.runId) return;
    setStatus(`Checking ${file.name}…`);
    try {
      const data = await inspectImageFile(file);
      if (runId !== state.runId) {
        if (data.previewUrl) URL.revokeObjectURL(data.previewUrl);
        return;
      }
      totalPixels += data.pixels;
      if (totalPixels > MAX_IMAGE_TOTAL_PIXELS) { URL.revokeObjectURL(data.previewUrl); throw new Error(`This batch exceeds the ${MAX_IMAGE_TOTAL_PIXELS / 1000000} megapixel decoded-image candidate limit.`); }
      state.imageEntries.push(createImageEntry(file, data));
    } catch (error) {
      if (runId !== state.runId) return;
      state.imageEntries.push(createImageEntry(file, null, error.message || 'The image could not be decoded.'));
    }
    renderApp();
  }
  state.working = false;
  setStatus('Images checked. Review the order and page settings.');
  renderApp();
}

async function imagesToPdf() {
  const entries = state.imageEntries.filter((entry) => entry.status === 'ready');
  if (!entries.length || state.working) return;
  state.working = true;
  const runId = ++state.runId;
  clearOutput();
  renderApp();
  try {
    const { PDFDocument, rgb, PageSizes } = await loadPdfLib();
    const pdf = await PDFDocument.create();
    const { paper, orientation } = state.imageSettings;
    const margin = Number(state.imageSettings.margin) || 0;
    for (const entry of entries) {
      if (runId !== state.runId) return;
      const image = entry.mime === 'image/png' ? await pdf.embedPng(entry.bytes) : await pdf.embedJpg(entry.bytes);
      let pageWidth;
      let pageHeight;
      if (paper === 'original') {
        pageWidth = entry.width;
        pageHeight = entry.height;
      } else {
        const size = paper === 'letter' ? PageSizes.Letter : PageSizes.A4;
        [pageWidth, pageHeight] = size;
        const imageLandscape = entry.width > entry.height;
        const pageLandscape = orientation === 'landscape' || (orientation === 'auto' && imageLandscape);
        if (pageLandscape && pageHeight > pageWidth) [pageWidth, pageHeight] = [pageHeight, pageWidth];
        if (orientation === 'portrait' && pageWidth > pageHeight) [pageWidth, pageHeight] = [pageHeight, pageWidth];
      }
      const page = pdf.addPage([pageWidth, pageHeight]);
      page.drawRectangle({ x: 0, y: 0, width: pageWidth, height: pageHeight, color: rgb(1, 1, 1) });
      const availableWidth = Math.max(1, pageWidth - margin * 2);
      const availableHeight = Math.max(1, pageHeight - margin * 2);
      const scale = Math.min(availableWidth / entry.width, availableHeight / entry.height);
      const width = entry.width * scale;
      const height = entry.height * scale;
      page.drawImage(image, { x: (pageWidth - width) / 2, y: (pageHeight - height) / 2, width, height });
    }
    const bytes = await pdf.save();
    await validatePdfOutput(bytes, entries.length);
    if (runId !== state.runId) return;
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    if (runId !== state.runId) {
      URL.revokeObjectURL(url);
      return;
    }
    state.output = { name: 'kivoza-images.pdf', url, size: bytes.length, pageCount: entries.length };
    setStatus(`Created a ${entries.length}-page PDF from ${entries.length} images.`, 'success');
  } catch (error) {
    setStatus(error.message || 'The images could not be written to a PDF.', 'error');
  } finally {
    if (runId === state.runId) {
      state.working = false;
      renderApp();
    }
  }
}

function findPdfEntry(id) {
  if (state.pdfSource?.id === id) return state.pdfSource;
  return state.mergeEntries.find((entry) => entry.id === id) || null;
}

async function renderThumbnail(canvas) {
  const entry = findPdfEntry(canvas.dataset.thumbEntry);
  const pageIndex = Number(canvas.dataset.thumbPage);
  if (!entry || entry.disposed || !entry.bytes || !Number.isInteger(pageIndex) || pageIndex < 0 || pageIndex >= entry.pageCount) return;
  if (canvas.dataset.rendered === 'true' || canvas.dataset.rendering === 'true') return;
  canvas.dataset.rendering = 'true';
  try {
    const pdfjs = await loadPdfJs();
    if (entry.disposed) return;
    if (!entry.thumbnailPdf) {
      if (!entry.thumbnailLoadingTask) entry.thumbnailLoadingTask = pdfjs.getDocument({ data: entry.bytes.slice(), cMapUrl: `${PDFJS_ASSET_BASE}cmaps/`, cMapPacked: true, standardFontDataUrl: `${PDFJS_ASSET_BASE}standard_fonts/`, wasmUrl: `${PDFJS_ASSET_BASE}wasm/` });
      const loadingTask = entry.thumbnailLoadingTask;
      const thumbnailPdf = await loadingTask.promise;
      if (entry.disposed) {
        if (thumbnailPdf && typeof thumbnailPdf.cleanup === 'function') {
          const cleanup = thumbnailPdf.cleanup();
          if (cleanup && typeof cleanup.catch === 'function') cleanup.catch(() => {});
        }
        return;
      }
      entry.thumbnailPdf = thumbnailPdf;
    }
    if (entry.disposed) return;
    const page = await entry.thumbnailPdf.getPage(pageIndex + 1);
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = Math.min(THUMBNAIL_WIDTH / baseViewport.width, 160 / baseViewport.height);
    const viewport = page.getViewport({ scale });
    const outputScale = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.max(1, Math.floor(viewport.width * outputScale));
    canvas.height = Math.max(1, Math.floor(viewport.height * outputScale));
    canvas.style.width = `${Math.floor(viewport.width)}px`;
    canvas.style.height = `${Math.floor(viewport.height)}px`;
    const context = canvas.getContext('2d', { alpha: false });
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: context, viewport, transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null, background: 'rgb(255,255,255)' }).promise;
    page.cleanup();
    canvas.dataset.rendered = 'true';
  } catch (error) {
    if (!entry.disposed && canvas.isConnected) {
      canvas.dataset.error = 'true';
      const fallback = makeElement('span', 'thumb-error', 'Preview unavailable');
      canvas.replaceWith(fallback);
    }
  } finally {
    delete canvas.dataset.rendering;
  }
}

function observeThumbnails() {
  const canvases = [...document.querySelectorAll('canvas[data-thumb-entry]')];
  if (!canvases.length) return;
  if (!('IntersectionObserver' in window)) { canvases.slice(0, 4).forEach((canvas) => renderThumbnail(canvas)); return; }
  state.thumbObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) { renderThumbnail(entry.target); state.thumbObserver.unobserve(entry.target); }
    });
  }, { rootMargin: '240px 0px' });
  canvases.forEach((canvas) => state.thumbObserver.observe(canvas));
}

function start() {
  renderApp();
}

start();
